import {
  isDefined,
  isEmpty,
  isNotEmpty,
  isRoleId,
  isTeamId,
  isUserId,
  type PermissionEntityScope,
  type PermissionList,
  type PermissionType,
  type RoleData,
  type RoleId,
  type TeamId,
  type UniversalSearchItem,
  type UserId,
} from '@terros/common'
import { MAX_PERMISSION_COMPARE_ENTITIES } from '@front/model'

export type CompareEntityId = UserId | TeamId | RoleId
const COMPARE_ENTITY_TYPES = ['user', 'team', 'role'] as const
export type CompareEntityKind = (typeof COMPARE_ENTITY_TYPES)[number]

export type CompareEntity =
  | { kind: 'user'; id: UserId; name: string }
  | { kind: 'team'; id: TeamId; name: string }
  | { kind: 'role'; id: RoleId; name: string }

export type CompareRoleOption = Pick<RoleData, 'roleId' | 'name'>

export type CompareEntityError = Error | string | undefined

export type CompareEntityOptionGroup = {
  group: CompareEntityKind
  items: { value: CompareEntityId; label: string }[]
}

type ComparisonPermissionList = Partial<PermissionList>

export const isCompareEntityId = (value: string): value is CompareEntityId =>
  isUserId(value) || isTeamId(value) || isRoleId(value)

export const normalizeCompareEntityIds = (values: string[]): CompareEntityId[] =>
  Array.from(new Set(values.filter(isCompareEntityId))).slice(0, MAX_PERMISSION_COMPARE_ENTITIES)

const searchItemToCompareEntity = (item: UniversalSearchItem): CompareEntity | undefined => {
  if (item.type === 'User') return { kind: 'user', id: item.userId, name: item.description }
  if (item.type === 'Team') return { kind: 'team', id: item.teamId, name: item.description }
  return undefined
}

export const toCompareEntity = ({ id, name }: { id: CompareEntityId; name: string }): CompareEntity => {
  if (isUserId(id)) return { kind: 'user', id, name }
  if (isTeamId(id)) return { kind: 'team', id, name }
  return { kind: 'role', id, name }
}

export const buildCompareEntityOptionGroups = ({
  searchResults,
  roles,
  search,
}: {
  searchResults: UniversalSearchItem[]
  roles: CompareRoleOption[]
  search: string
}): CompareEntityOptionGroup[] => {
  const normalizedSearch = search.toLocaleLowerCase()
  if (isEmpty(normalizedSearch)) return []
  const searchedEntities = searchResults.map(searchItemToCompareEntity).filter(isDefined)
  const matchingRoles = roles
    .filter((role) => role.name.toLocaleLowerCase().includes(normalizedSearch))
    .map((role): CompareEntity => ({ kind: 'role', id: role.roleId, name: role.name }))
  const entities = Array.from(
    new Map([...searchedEntities, ...matchingRoles].map((entity) => [entity.id, entity])).values()
  )

  return COMPARE_ENTITY_TYPES.map((kind) => ({
    group: kind,
    items: entities
      .filter((entity) => entity.kind === kind)
      .map((entity) => ({ value: entity.id, label: entity.name })),
  })).filter((group) => isNotEmpty(group.items))
}

export const getComparisonPermissionScope = (
  permissionList: ComparisonPermissionList | undefined,
  permissionType: PermissionType
): PermissionEntityScope => permissionList?.[permissionType]?.primaryScope?.entity ?? 'none'

export const isPermissionValueDifferentFromControl = (
  control: ComparisonPermissionList | undefined,
  compared: ComparisonPermissionList | undefined,
  permissionType: PermissionType
): boolean => {
  if (!isDefined(control) || !isDefined(compared)) return false
  return (
    getComparisonPermissionScope(compared, permissionType) !== getComparisonPermissionScope(control, permissionType)
  )
}

export const isPermissionDifferentFromControl = (
  permissionLists: (ComparisonPermissionList | undefined)[],
  permissionType: PermissionType
): boolean => {
  const [control, ...compared] = permissionLists
  if (!control) return false
  return compared.some((permissionList) =>
    isPermissionValueDifferentFromControl(control, permissionList, permissionType)
  )
}
